import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { RedactionSettingsService } from './proxy/redaction-settings.service';
import {
  RedactionPropertyDto,
  RedactionSettingCreateOrUpdateDto,
  RedactionSettingDto,
  RevealMode,
} from './proxy/redaction-settings.model';
import { ToasterService } from '@abp/ng.theme.shared';
import { ConfigStateService } from '@abp/ng.core';
import { Validators } from '@angular/forms';

@Component({
  selector: 'cai-redaction-settings',
  templateUrl: './redaction-settings.component.html',
  styleUrls: ['./redaction-settings.component.scss'],
  standalone: false,
})
export class RedactionSettingsComponent implements OnInit {
  form: UntypedFormGroup;
  isLoading: boolean;
  cultureName: string;
  redactionSettings: RedactionSettingDto;
  expandedProperties = new Set<string>();
  defaultRevealCount = 0;

  constructor(
    private redactionSettingsService: RedactionSettingsService,
    private fb: UntypedFormBuilder,
    private toaster: ToasterService,
    private configStateService: ConfigStateService,
  ) {}

  ngOnInit(): void {
    this.buildForm();
  }

  buildForm() {
    this.cultureName = this.configStateService.getOne('localization')?.currentCulture.cultureName;

    let previousPropertyValues: { [key: string]: any } = {};
    if (this.form && this.form.get('properties')) {
      previousPropertyValues = (this.form.get('properties') as FormGroup).value;
    }

    this.redactionSettingsService.get().subscribe({
      next: (redactionSettings) => {
        this.redactionSettings = redactionSettings;

        if (this.redactionSettings.isServiceUnavailable) {
          this.form = this.fb.group({
            isServiceEnabled: [this.redactionSettings.isServiceEnabled],
          });

          this.toaster.error('Exception::Raven:0100163');

          return;
        }

        this.form = this.fb.group({
          isServiceEnabled: [this.redactionSettings.isServiceEnabled],
          type: [this.redactionSettings.type.toString()],
          mode: [this.redactionSettings.mode.toString()],
          properties: this.fb.group({}),
        });

        this.redactionSettings.properties.forEach((property: RedactionPropertyDto) => {
          const prev = previousPropertyValues || {};
          (this.form.get('properties') as FormGroup).addControl(
            property.phraseType,
            new FormControl(
              prev[property.phraseType] !== undefined
                ? prev[property.phraseType]
                : property.isSelected,
            ),
          );

          const { revealMode, revealCount } = this.getRevealModeAndCount(property);

          (this.form.get('properties') as FormGroup).addControl(
            property.phraseType + '_revealMode',
            new FormControl(
              prev[property.phraseType + '_revealMode'] !== undefined
                ? prev[property.phraseType + '_revealMode']
                : revealMode,
            ),
          );
          const countControl = new FormControl(
            prev[property.phraseType + '_revealCount'] !== undefined
              ? prev[property.phraseType + '_revealCount']
              : (revealCount ?? this.defaultRevealCount),
            [Validators.min(0), Validators.max(10)],
          );

          countControl.valueChanges.subscribe((val) => {
            if (val === null || val === undefined || val === '') {
              countControl.setValue(0, { emitEvent: false });
              return;
            }
            if (val < 0) {
              countControl.setValue(0, { emitEvent: false });
              return;
            }
            if (val > 10) {
              countControl.setValue(10, { emitEvent: false });
              return;
            }
          });

          (this.form.get('properties') as FormGroup).addControl(
            property.phraseType + '_revealCount',
            countControl,
          );

          (this.form.get('properties') as FormGroup).addControl(
            property.phraseType + '_revealStartCount',
            new FormControl(property.revealStartChars ?? 0, [
              Validators.min(0),
              Validators.max(10),
            ]),
          );
          (this.form.get('properties') as FormGroup).addControl(
            property.phraseType + '_revealEndCount',
            new FormControl(property.revealEndChars ?? 0, [Validators.min(0), Validators.max(10)]),
          );
        });

        this.form.addControl('selectAll', new FormControl(this.areAllPropertiesSelected()));
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  submitForm() {
    this.isLoading = true;
    const input: RedactionSettingCreateOrUpdateDto = {
      isServiceEnabled: this.form.get('isServiceEnabled').value,
    };

    if (!this.redactionSettings.isServiceUnavailable) {
      const propertiesFormGroup = this.form.get('properties') as FormGroup;
      input.type = this.form.get('type').value;
      input.mode = this.form.get('mode').value;
      input.redactionPhraseTypeSettings = Object.keys(propertiesFormGroup.controls)
        .filter(
          (key) =>
            !key.endsWith('_revealMode') &&
            !key.endsWith('_revealCount') &&
            !key.endsWith('_revealStartCount') &&
            !key.endsWith('_revealEndCount'),
        )
        .filter((phraseType) => propertiesFormGroup.get(phraseType).value)
        .map((phraseType) => {
          const revealMode = propertiesFormGroup.get(phraseType + '_revealMode')?.value;
          const revealCount = propertiesFormGroup.get(phraseType + '_revealCount')?.value;
          return {
            phraseType,
            revealStartChars: revealMode === RevealMode.Start ? revealCount : null,
            revealEndChars: revealMode === RevealMode.End ? revealCount : null,
          };
        });
    }

    this.redactionSettingsService.createOrUpdate(input).subscribe({
      next: () => {
        this.isLoading = false;
        this.toaster.success('AbpUi::SavedSuccessfully');
      },
      error: (error) => {
        console.log(error);
        this.isLoading = false;
        this.buildForm();
        this.toaster.error('AbpExceptionHandling::InternalServerErrorMessage');
      },
    });
  }

  getDisplayName(property: RedactionPropertyDto) {
    const displayName = property.phraseInformationList.find(
      (x) => x.languageCode === this.cultureName,
    )?.displayName;

    return displayName;
  }

  getLanguageCodes(property: RedactionPropertyDto) {
    const languageCodes = property.languageCodes.join(',');

    return `(${languageCodes})`;
  }

  onClickedSelectAll(value: boolean) {
    this.form.get('selectAll').setValue(value);
    const propertiesFormGroup = this.form.get('properties') as FormGroup;
    Object.keys(propertiesFormGroup.controls)
      .filter(
        (key) =>
          !key.endsWith('_revealMode') &&
          !key.endsWith('_revealCount') &&
          !key.endsWith('_revealStartCount') &&
          !key.endsWith('_revealEndCount'),
      )
      .forEach((phraseType) => {
        propertiesFormGroup.get(phraseType).setValue(value);
        if (value) {
          if (!propertiesFormGroup.get(phraseType + '_revealMode').value) {
            propertiesFormGroup.get(phraseType + '_revealMode').setValue(RevealMode.Off);
          }
          if (
            propertiesFormGroup.get(phraseType + '_revealCount').value === null ||
            propertiesFormGroup.get(phraseType + '_revealCount').value === undefined
          ) {
            propertiesFormGroup.get(phraseType + '_revealCount').setValue(0);
          }
        }
      });
  }

  onPhraseTypeChanged() {
    this.form.get('selectAll').setValue(this.areAllPropertiesSelected());
  }

  onRevealChanged(
    phraseType: string,
    event: { mode: RevealMode; count: number; startCount: number; endCount: number },
  ) {
    const group = this.form.get('properties') as FormGroup;
    group.patchValue({
      [phraseType + '_revealMode']: event.mode,
      [phraseType + '_revealCount']: event.count,
      [phraseType + '_revealStartCount']: event.startCount,
      [phraseType + '_revealEndCount']: event.endCount,
    });
  }

  areAllPropertiesSelected(): boolean {
    const propertiesFormGroup = this.form.get('properties') as FormGroup;
    return Object.keys(propertiesFormGroup.controls)
      .filter((key) => !key.endsWith('_revealMode'))
      .every((phraseType) => propertiesFormGroup.get(phraseType).value === true);
  }

  toggleExpand(phraseType: string) {
    if (this.expandedProperties.has(phraseType)) {
      this.expandedProperties.delete(phraseType);
    } else {
      this.expandedProperties.add(phraseType);
    }
  }

  isExpanded(phraseType: string): boolean {
    return this.expandedProperties.has(phraseType);
  }

  private getRevealModeAndCount(property: RedactionPropertyDto): {
    revealMode: RevealMode;
    revealCount: number;
  } {
    if (property.revealStartChars !== null && property.revealStartChars !== undefined) {
      return { revealMode: RevealMode.Start, revealCount: property.revealStartChars };
    }
    if (property.revealEndChars !== null && property.revealEndChars !== undefined) {
      return { revealMode: RevealMode.End, revealCount: property.revealEndChars };
    }
    return { revealMode: RevealMode.Off, revealCount: 0 };
  }
}
